import { getCatalogCategories } from '../constants/catalog-categories.constants';
import { RANGE_LAST_30_DAYS, RANGE_LAST_90_DAYS } from '../constants/default-date-range';
import { AppsService } from '@/common/apps/apps.service';
import { getNpsWidgetCatalogSettings } from '../widgets/nps-widget/nps-widget-options.js';
import { getPollWidgetCatalogSettings } from '../widgets/poll-results-v2/util/config.js';
import { SET_DEFAULT_APP } from '../constants/default-app';
import { METADATA_WIDGET_CATALOG_SETTINGS } from '@/metadata/widget-utils.js';
import {
    getPesWidgetCatalogSettings,
    getPesTrendingWidgetCatalogSettings,
} from '@/product-engagement-score/widget-pes-utils.js';
import { PS_CUSTOM_WIDGET_SETTINGS, PS_CUSTOM_WIDGET_SETTINGS_AEU } from '../widgets/professional-services-custom-widget/agg.utils.js';
import { DEFAULT_ACCOUNT_SETTING } from '../constants/default-account';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import {
    averageDailyTimeOnAppAgg,
    averageDailyTimeOnAppAeuAgg,
    averageWeeklyTimeOnAppAgg,
    averageWeeklyTimeOnAppAeuAgg,
    top10FeaturesByEventsAgg,
    top10FeaturesByEventsAeuAgg,
    top10FeaturesByVisitorsAgg,
    top10FeaturesByVisitorsAeuAgg,
    top10PagesByEventsAgg,
    top10PagesByEventsAeuAgg,
    top10PagesByVisitorsAgg,
    top10PagesByVisitorsAeuAgg,
} from '../widgets/professional-services-custom-widget/ps-agg-starters';
import {
    top10FeaturesByVisitorsConfig,
    top10FeaturesByEventsConfig,
    avgDailyTimeOnAppConfig,
    avgWeeklyTimeOnAppConfig,
    top10PagesByEventsConfig,
} from '../widgets/professional-services-custom-widget/ps-highcharts-config';
import { i18n_t as $t } from '@utils/i18n';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';
const { ALL_AGG_ID, WEB_AGG_ID, MOBILE_AGG_ID, legacyAppId } = AppsService;

export default ({ productName = 'Pendo' } = {}) => {
    const {
        VISITORS,
        GENERAL,
        SYSTEM,
        FEEDBACK,
        FEATURES,
        PAGES,
        TRACK_EVENTS,
        ACCOUNTS,
        AGENT_ANALYTICS,
        GUIDES,
        MOBILE,
        REPORTS,
        REPLAY,
    } = getCatalogCategories();

    const accountsUIEnabled = activeSubHasPurchasedProductLine('core', 'accountsUIEnabled');
    const isAdoptEndUser = activeSubHasPurchasedProductLine('adoptEndUser');

    const settings = accountsUIEnabled ? {
        appId: ALL_AGG_ID,
        segmentId: 'everyone',
        account: DEFAULT_ACCOUNT_SETTING,
    } : {
        appId: ALL_AGG_ID,
        segmentId: 'everyone',
    };

    return [
        {
            name: $t('widgetCatalogSettings.Active Unique Visitors Per Hour'),
            description: $t(
                'widgetCatalogSettings.Chart visualizing the active unique visitors per hour in sub- specific timezone. Active time includes clicks, page views, etc. no idle time for the trailing calendar week.',
            ),
            type: 'activeVisitorsHourlyChart',
            image: 'active-unique-visitors.png',
            categories: [VISITORS],
            settings,
            isDefault: true,
            id: 'activeVisitorsHourlyChart',
        },
        {
            name: $t('widgetCatalogSettings.App Usage'),
            description: $t(
                'widgetCatalogSettings.View percentages based on aggregated time on site for each application and in relation to all the others.',
            ),
            type: 'appUsage',
            image: 'pie.png',
            categories: [GENERAL],
            settings: {
                ...settings,
                dateRange: RANGE_LAST_30_DAYS,
            },
            isMultiAppOnly: true,
            id: 'appUsage',
        },
        {
            name: $t('widgetCatalogSettings.Browser Usage'),
            description: $t(
                'widgetCatalogSettings.Visitor browser usage - info we are auto-gathering from the user agent when receiving events from visitors (browser language, browser name, operating system, etc.).',
            ),
            type: 'browserUsageChart',
            image: 'pie.png',
            categories: [SYSTEM],
            settings: {
                ...settings,
                dateRange: RANGE_LAST_30_DAYS,
                appId: WEB_AGG_ID,
            },
            isDefault: true,
            id: 'browserUsageChart',
        },
        {
            name: $t('widgetCatalogSettings.Product Requests By Status'),
            description: $t('widgetCatalogSettings.Displays the percentage of requests per status'),
            type: 'productRequestsByStatus',
            image: 'product-requests-by-status.png',
            categories: [FEEDBACK],
            settings: {
                appId: ALL_AGG_ID,
            },
            isFeedback: true,
            id: 'productRequestsByStatus',
        },
        {
            name: $t('widgetCatalogSettings.Time to First Use'),
            description: $t(
                'widgetCatalogSettings.Understand the distribution of time and average time it takes visitors to first engage with one or more selected events, visualized as a box plot.',
            ),
            type: 'timeToFirstUse',
            image: 'time-to-first-use.png',
            categories: [FEATURES, PAGES, TRACK_EVENTS],
            settings: accountsUIEnabled ? {
                dateRange: {
                    timeSeries: {
                        config: {
                            type: 'Last 90 Days',
                        },
                        spec: {
                            count: 90,
                        },
                    },
                    type: 'Last 90 Days',
                    tags: [],
                    useCoreEvents: false,
                },
                segmentId: 'everyone',
                appId: [SET_DEFAULT_APP],
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                dateRange: {
                    timeSeries: {
                        config: {
                            type: 'Last 90 Days',
                        },
                        spec: {
                            count: 90,
                        },
                    },
                    type: 'Last 90 Days',
                    tags: [],
                    useCoreEvents: false,
                },
                segmentId: 'everyone',
                appId: [SET_DEFAULT_APP],
            },
            isPaidPendoOnly: true,
            productLines: [PRODUCT_LINES.ANALYTICS],
            id: 'timeToFirstUse',
        },
        {
            name: $t('widgetCatalogSettings.Retention by First Use'),
            description: accountsUIEnabled ? $t(
                'widgetCatalogSettings.Understand how often visitors or accounts return to your app after they generate a specific event. Retention curve is based on the usage of events within the first 30 days.',
            ) : $t(
                'widgetCatalogSettings.Understand how often visitors return to your app after they generate a specific event. Retention curve is based on the usage of events within the first 30 days.',
            ),
            type: 'retentionByUsage',
            image: 'retention-by-usage.png',
            categories: accountsUIEnabled ? [FEATURES, PAGES, TRACK_EVENTS, VISITORS, ACCOUNTS] : [FEATURES, PAGES, TRACK_EVENTS, VISITORS],
            settings: {
                eventFirstUsedWithin: 'first30Days',
                kind: accountsUIEnabled ? 'account' : 'visitor',
                frequency: 'month',
                frequencyTimespan: 12,
                segmentId: 'everyone',
                appId: [SET_DEFAULT_APP],
                tags: [],
                useCoreEvents: false,
            },
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'retentionEnabled']],
            id: 'retentionByUsage',
        },
        getPesWidgetCatalogSettings(),
        getPesTrendingWidgetCatalogSettings(),
        {
            name: $t('widgetCatalogSettings.Recent Product Requests'),
            description: $t('widgetCatalogSettings.Displays recently created product requests.'),
            type: 'recentProductRequests',
            image: 'recent-product-requests.png',
            categories: [FEEDBACK],
            settings: {
                appId: ALL_AGG_ID,
                displayCount: 5,
            },
            isFeedback: true,
            id: 'recentProductRequests',
        },
        {
            name: $t('widgetCatalogSettings.Product Requests By Product Area'),
            description: $t(
                'widgetCatalogSettings.Displays product requests associated with each Product Area, where the larger the treemap box the more requests for that area.',
            ),
            type: 'productRequestsByGroup',
            image: 'product-requests-by-group.png',
            categories: [FEEDBACK],
            settings: {
                appId: ALL_AGG_ID,
            },
            isFeedback: true,
            id: 'productRequestsByGroup',
        },
        {
            name: $t('widgetCatalogSettings.Data Explorer'),
            description: $t(
                'widgetCatalogSettings.Gain insights into user behavior by querying event, guide, or app data and visualizing trends. Filter by metadata and event properties, compare segments and date ranges, or add formulas.',
            ),
            type: 'customReportChart',
            image: 'data-explorer.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'dataExplorerEnabled']],
            id: 'customReportChart',
        },
        {
            name: $t('widgetCatalogSettings.Funnel'),
            description: $t(
                'widgetCatalogSettings.Analyze conversion rate and completion time across a set of specified steps, measured by total attempts or unique visitors, and compare those results among different groups.',
            ),
            type: 'funnelReportChart',
            image: 'funnel.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'funnelsEnabled']],
            id: 'funnelReportChart',
        },
        {
            name: $t('widgetCatalogSettings.Path'),
            description: $t(
                'widgetCatalogSettings.Visualize user journeys starting from or leading to a specified event to understand most common paths users take throughout your app.',
            ),
            type: 'pathReportChart',
            image: 'path.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'pathsEnabled']],
            id: 'pathReportChart',
        },
        {
            name: $t('widgetCatalogSettings.Retention'),
            description: $t(
                'widgetCatalogSettings.Understand what percentage of your users return to an app or a specific feature, providing insights into customer satisfaction and long-term engagement.',
            ),
            type: 'retentionReportChart',
            image: 'retention-report-chart.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'retentionEnabled']],
            id: 'retentionReportChart',
        },
        {
            name: $t('widgetCatalogSettings.Device Usage'),
            description: $t(
                'widgetCatalogSettings.Visitor device usage - info we are auto-gathering from the user agent when receiving events from visitors (browser language, browser name, operating system, etc.).',
            ),
            type: 'deviceUsage',
            image: 'pie.png',
            categories: [SYSTEM],
            settings: {
                ...settings,
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'deviceUsage',
        },
        {
            name: $t('widgetCatalogSettings.App Version Distribution'),
            description: $t(
                'widgetCatalogSettings.Shows the percentage of users per app version for any selected date range. Note: Only the latest app version from the user is will be calculated.',
            ),
            type: 'appVersion',
            image: 'pie.png',
            categories: [MOBILE],
            settings: accountsUIEnabled ? {
                dateRange: RANGE_LAST_30_DAYS,
                appId: MOBILE_AGG_ID,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                dateRange: RANGE_LAST_30_DAYS,
                appId: MOBILE_AGG_ID,
                segmentId: 'everyone',
            },
            isMobileOnly: true,
            id: 'appVersion',
        },
        {
            name: $t('widgetCatalogSettings.OS Version Distribution'),
            description: $t(
                'widgetCatalogSettings.Shows the percentage of users per OS version for any selected date range. Filter by a specific app or by all mobile apps.',
            ),
            type: 'osVersion',
            image: 'pie.png',
            categories: [MOBILE],
            settings: accountsUIEnabled ? {
                dateRange: RANGE_LAST_30_DAYS,
                appId: MOBILE_AGG_ID,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                dateRange: RANGE_LAST_30_DAYS,
                appId: MOBILE_AGG_ID,
                segmentId: 'everyone',
            },
            isMobileOnly: true,
            id: 'osVersion',
        },
        {
            name: $t('widgetCatalogSettings.Visitor Frequency'),
            description: $t(
                'widgetCatalogSettings.Show visitor activity categorized by High (at least 13 days), Medium (min of 5, max of 12 days), or Low (min of 1, max of 4 days) of the last 30 days.',
            ),
            type: 'dwmChart',
            image: 'visitor-account-frequency.png',
            categories: [VISITORS],
            settings: {
                ...settings,
                kind: 'visitor',
            },
            id: 'visitorFrequency',
        },
        {
            name: $t('widgetCatalogSettings.Account Frequency'),
            description: $t(
                'widgetCatalogSettings.Show account activity categorized by High (at least 13 days), Medium (min of 5, max of 12 days), or Low (min of 1, max of 4 days) of the last 30 days.',
            ),
            type: 'dwmChart',
            image: 'visitor-account-frequency.png',
            categories: [ACCOUNTS],
            settings: {
                kind: 'account',
                appId: ALL_AGG_ID,
                segmentId: 'everyone',
            },
            id: 'accountFrequency',
        },
        {
            name: $t('widgetCatalogSettings.Feature Adoption'),
            description: $t(
                'widgetCatalogSettings.Identify which features generate 80% of click volume within a specified timeframe.',
            ),
            type: 'featureAdoption',
            image: 'feature-adoption.png',
            categories: [FEATURES],
            settings: {
                ...settings,
                benchmark: 80,
                dateRange: RANGE_LAST_30_DAYS,
                metric: 'denormalized',
                tagType: 'feature',
            },
            id: 'featureAdoption',
        },
        {
            name: $t('widgetCatalogSettings.Goals'),
            description: $t(
                'widgetCatalogSettings.Surfaces Ongoing or Timebound usage goals on the dashboard where they can be tracked and shared with other product analytics.',
            ),
            type: 'goalProgress',
            image: 'goal-progress.png',
            categories: [GENERAL],
            settings: {
                appId: ALL_AGG_ID,
            },
            isPaidPendoOnly: false,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'goalsEnabled']],
            id: 'goalProgress',
        },
        {
            name: $t('widgetCatalogSettings.Guide Activity'),
            description: $t(
                'widgetCatalogSettings.View all guide events for a specific guide, or guide events per visitor',
            ),
            type: 'guideActivity',
            image: 'guide-activity.png',
            categories: [GUIDES],
            settings: {
                segmentId: 'everyone',
                dateRange: RANGE_LAST_30_DAYS,
                groupByVisitor: true,
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled']],
            id: 'guideActivity',
        },
        {
            name: $t('widgetCatalogSettings.Guide Overview'),
            description: $t('widgetCatalogSettings.numActiveGuides'),
            type: 'guideOverview',
            image: 'guide-overview.png',
            categories: [GUIDES],
            settings: {
                appId: ALL_AGG_ID,
            },
            isDefault: true,
            productLines: [PRODUCT_LINES.GUIDES],
            id: 'guideOverview',
        },
        {
            name: $t('widgetCatalogSettings.Guide Experiment Summary'),
            type: 'guideExperimentSummaryDashboard',
            image: 'guide-overview.png',
            categories: [GUIDES],
            settings: accountsUIEnabled ? {
                guideId: null,
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                guideId: null,
            },
            hideFromMenu: true,
            productLines: [PRODUCT_LINES.GUIDES],
            id: 'guideExperimentSummary',
        },
        {
            name: $t('widgetCatalogSettings.Most Seen Guides'),
            description: $t(
                'widgetCatalogSettings.List of the top 5 most viewed guides per a specified date range and segment selected.',
            ),
            type: 'mostSeenGuides',
            image: 'most-seen-guides.png',
            categories: [GUIDES],
            settings: {
                ...settings,
                dateRange: RANGE_LAST_30_DAYS,
            },
            isDefault: true,
            productLines: [PRODUCT_LINES.GUIDES],
            id: 'mostSeenGuides',
        },
        {
            name: $t('widgetCatalogSettings.Guide Effectiveness Metrics'),
            description: $t(
                'widgetCatalogSettings.View aggregate guide effectiveness metrics including views, visitors, goal adoption, engagement, and completion rates.',
            ),
            type: 'guideEffectivenessMetrics',
            image: 'guide-overview.png',
            categories: [GUIDES],
            settings: {
                appId: legacyAppId,
                dateRange: RANGE_LAST_30_DAYS,
                productAreaIds: [],
                categories: [],
                statuses: [],
                pages: [],
                activations: [],
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideEffectivenessEnabled']],
            id: 'guideEffectivenessMetrics',
        },
        {
            name: $t('widgetCatalogSettings.Top Guide Metrics'),
            description: $t(
                'widgetCatalogSettings.View key guide metrics, including first-time views, total views, and average time viewed.',
            ),
            type: 'guideTopMetrics',
            image: 'guide-top-metrics.png',
            categories: [GUIDES],
            settings: {
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled']],
            id: 'guideTopMetrics',
        },
        {
            name: $t('widgetCatalogSettings.Guide Elements'),
            description: $t('widgetCatalogSettings.Understand the use of building block elements in a guide.'),
            type: 'guideElements',
            image: 'guide-elements.png',
            categories: [GUIDES],
            settings: {
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
                groupByStep: true,
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled']],
            id: 'guideElements',
        },
        {
            name: $t('widgetCatalogSettings.Time On Guide'),
            description: $t(
                'widgetCatalogSettings.View the average number of seconds that a guide displayed each time it was viewed.',
            ),
            type: 'timeOnGuide',
            image: 'time-on-guide.png',
            categories: [GUIDES],
            settings: {
                segmentId: 'everyone',
                dateRange: RANGE_LAST_30_DAYS,
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled']],
            id: 'timeOnGuide',
        },
        {
            name: $t('widgetCatalogSettings.Guide Goal'),
            description: $t(
                'widgetCatalogSettings.Keep track of your guide goals and see how many visitors performed a specific action after viewing the guide.',
            ),
            type: 'guideGoal',
            image: 'guide-goal.png',
            categories: [GUIDES],
            settings: {},
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled'], [PRODUCT_LINES.GUIDES, 'guideGoalsEnabled']],
            id: 'guideGoal',
        },
        {
            name: $t('widgetCatalogSettings.Operating System Usage'),
            description: $t(
                'widgetCatalogSettings.Visitor specific data, info we are auto-gathering from the user agent when receiving events from visitors (browser language, browser name, operating system, etc.).',
            ),
            type: 'osChart',
            image: 'pie.png',
            categories: [SYSTEM],
            settings: {
                ...settings,
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'osChart',
        },
        {
            name: $t('widgetCatalogSettings.Weekly Visitors'),
            description: $t(
                'widgetCatalogSettings.Number of active visitors trended weekly over a specified time. Configurable to use days or months as well.',
            ),
            type: 'perWeekChart',
            image: 'area.png',
            categories: [VISITORS],
            settings: {
                ...settings,
                kind: 'visitor',
                period: 'weekly',
                dateRange: RANGE_LAST_90_DAYS,
            },
            isDefault: true,
            id: 'weeklyVisitors',
        },
        {
            name: $t('widgetCatalogSettings.Weekly Accounts'),
            description: $t(
                'widgetCatalogSettings.Number of active accounts trended weekly over a specified time. Configurable to use days or months as well.',
            ),
            type: 'perWeekChart',
            image: 'area.png',
            categories: [ACCOUNTS],
            settings: {
                ...settings,
                kind: 'account',
                period: 'weekly',
                dateRange: RANGE_LAST_90_DAYS,
            },
            isDefault: true,
            id: 'weeklyAccounts',
        },
        {
            name: $t('widgetCatalogSettings.Total Time On App Per Week'),
            description: accountsUIEnabled ? $t(
                'widgetCatalogSettings.Can set this up to track weekly/monthly time on site, total clicks/views (events, visitors or accounts).',
            ) : $t(
                'widgetCatalogSettings.Can set this up to track weekly/monthly time on site, total clicks/views (events or visitors).',
            ),
            type: 'perWeekChart',
            image: 'area.png',
            categories: [GENERAL],
            settings: {
                ...settings,
                kind: 'eventTime',
                period: 'weekly',
                dateRange: RANGE_LAST_90_DAYS,
            },
            id: 'totalTimeOnAppPerWeek',
        },
        {
            name: $t('widgetCatalogSettings.Feature Use by Visitor'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of feature usage when measuring # of visitors using and # of click from each visitor that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [FEATURES, VISITORS],
            settings: {
                ...settings,
                tagType: 'feature',
                kind: 'visitor',
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'featureUseByVisitor',
        },
        {
            name: $t('widgetCatalogSettings.Feature Use by Account'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of feature usage when measuring # of accounts using and # of clicks from each account that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [ACCOUNTS, FEATURES],
            settings: {
                ...settings,
                tagType: 'feature',
                kind: 'account',
                dateRange: RANGE_LAST_30_DAYS,
            },
            isDefault: true,
            id: 'featureUseByAccount',
        },
        {
            name: $t('widgetCatalogSettings.Page Use by Visitor'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of page views when measuring # of visitors viewing and # of views from each visitor that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [PAGES, VISITORS],
            settings: {
                ...settings,
                tagType: 'page',
                kind: 'visitor',
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'pageUseByVisitor',
        },
        {
            name: $t('widgetCatalogSettings.Page Use by Account'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of page views when measuring # of accounts viewing and # of views from each account that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [ACCOUNTS, PAGES],
            settings: {
                ...settings,
                tagType: 'page',
                kind: 'account',
                dateRange: RANGE_LAST_30_DAYS,
            },
            isDefault: true,
            id: 'pageUseByAccount',
        },
        {
            name: $t('widgetCatalogSettings.Track Event Use by Visitor'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of track event usage when measuring # of visitors using and # of click from each visitor that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [TRACK_EVENTS, VISITORS],
            settings: {
                ...settings,
                tagType: 'trackType',
                kind: 'visitor',
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'trackEventUseByVisitor',
        },
        {
            name: $t('widgetCatalogSettings.Track Event Use by Account'),
            description: $t(
                'widgetCatalogSettings.Scatterplot of track event usage when measuring # of accounts using and # of clicks from each account that fit the applied segment and date range.',
            ),
            type: 'scatterChart',
            image: 'scatter.png',
            categories: [ACCOUNTS, TRACK_EVENTS],
            settings: {
                ...settings,
                tagType: 'trackType',
                kind: 'account',
                dateRange: RANGE_LAST_30_DAYS,
            },
            id: 'trackEventUseByAccount',
        },
        {
            name: $t('widgetCatalogSettings.Stickiness Metric'),
            description: accountsUIEnabled ? $t(
                'widgetCatalogSettings.Visualize visitor or account engagement as a ratio of short term versus long term return frequency. Configure the chart to show ratios of daily, weekly, or monthly active users or accounts.',
            ) : $t(
                'widgetCatalogSettings.Visualize visitor engagement as a ratio of short term versus long term return frequency. Configure the chart to show ratios of daily, weekly, or monthly active users.',
            ),
            type: 'stickinessMetric',
            image: 'stickiness.png',
            categories: accountsUIEnabled ? [ACCOUNTS, VISITORS] : [VISITORS],
            searchKeywords: accountsUIEnabled ? ['DAU', 'WAU', 'MAU', 'DAA', 'WAA', 'MAA'] : ['DAU', 'WAU', 'MAU'],
            settings: {
                ...settings,
                metric: 'DAU/MAU',
                weekendsHidden: false,
                dateRange: RANGE_LAST_30_DAYS,
                period: 'daily',
            },
            productLines: [PRODUCT_LINES.ANALYTICS],
            isPaidPendoOnly: true,
            id: 'stickinessMetric',
        },
        {
            name: $t('widgetCatalogSettings.Page Overview'),
            description: $t('widgetCatalogSettings.Total number of tagged pages.'),
            type: 'tagOverview',
            image: 'page-overview.png',
            categories: [PAGES],
            settings: {
                kind: 'page',
                appId: ALL_AGG_ID,
            },
            isDefault: true,
            id: 'pageOverview',
        },
        {
            name: $t('widgetCatalogSettings.Feature Overview'),
            description: $t('widgetCatalogSettings.Total number of features tagged.'),
            type: 'tagOverview',
            image: 'feature-overview.png',
            categories: [FEATURES],
            settings: {
                kind: 'feature',
                appId: ALL_AGG_ID,
            },
            isDefault: true,
            id: 'featureOverview',
        },
        {
            name: $t('widgetCatalogSettings.Visitor Overview'),
            description: $t('widgetCatalogSettings.Unique visitors for today, yesterday, last 7 days & last 30 days.'),
            type: 'visitorOverview',
            image: 'multi-overview.png',
            categories: [VISITORS],
            settings: {
                ...settings,
                kind: 'visitor',
            },
            isDefault: true,
            id: 'visitorOverview',
        },
        {
            name: $t('widgetCatalogSettings.Account Overview'),
            description: $t(
                'widgetCatalogSettings.Unique account visits for today, yesterday, last 7 days & last 30 days.',
            ),
            type: 'visitorOverview',
            image: 'multi-overview.png',
            categories: [ACCOUNTS],
            settings: {
                kind: 'account',
                appId: ALL_AGG_ID,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            },
            isDefault: true,
            id: 'accountOverview',
        },
        {
            name: $t('widgetCatalogSettings.Page/Feature Use Over Time'),
            description: $t(
                'widgetCatalogSettings.Visualize data for multiple pages & features, based on the applied segment and date range, and analyze in a line chart.',
            ),
            type: 'pageFeatureUseOverTime',
            image: 'page-feature-use.png',
            categories: [FEATURES, PAGES, TRACK_EVENTS],
            settings: {
                ...settings,
                dateRange: {
                    type: 'Last 7 Days',
                },
                events: {},
                kind: 'eventCount',
                period: 'daily',
                appId: [ALL_AGG_ID],
            },
            id: 'pageFeatureUseOverTime',
        },
        {
            name: $t('widgetCatalogSettings.Page key metrics'),
            description: $t('widgetCatalogSettings.See key metrics and trends for a specific Page.'),
            type: 'countableMetric',
            image: 'page-key-metrics.png',
            categories: [GENERAL, PAGES],
            settings: accountsUIEnabled ? {
                eventType: 'page',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                eventType: 'page',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
            },
            id: 'pageKeyMetrics',
        },
        {
            name: $t('widgetCatalogSettings.Feature key metrics'),
            description: $t('widgetCatalogSettings.See key metrics and trends for a specific Feature.'),
            type: 'countableMetric',
            image: 'feature-key-metrics.png',
            categories: [GENERAL, FEATURES],
            settings: accountsUIEnabled ? {
                eventType: 'feature',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                eventType: 'feature',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
            },
            id: 'featureKeyMetrics',
        },
        {
            name: $t('widgetCatalogSettings.Track event key metrics'),
            description: $t('widgetCatalogSettings.See key metrics and trends for a specific Track Event.'),
            type: 'countableMetric',
            image: 'track-event-key-metrics.png',
            categories: [GENERAL, TRACK_EVENTS],
            settings: accountsUIEnabled ? {
                eventType: 'trackType',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
            } : {
                eventType: 'trackType',
                dateRange: RANGE_LAST_30_DAYS,
                segmentId: 'everyone',
            },
            id: 'trackEventKeyMetrics',
        },
        getPollWidgetCatalogSettings(),
        {
            name: $t('widgetCatalogSettings.Text Block'),
            description: $t(
                'widgetCatalogSettings.Free-edit text field which provides the ability to include important context alongside dashboard data.',
            ),
            type: 'richTextWidget',
            image: 'rich-text.png',
            categories: [GENERAL],
            settings: {},
            id: 'textBlock',
        },
        METADATA_WIDGET_CATALOG_SETTINGS,
        {
            name: $t('widgetCatalogSettings.Embed IFrame Code'),
            description: $t(
                'widgetCatalogSettings.Add and display iframe content on any {productName} Dashboard. Include more information from external sources and add context to product data.',
                { productName: isAdoptEndUser ? productName : 'Pendo' },
            ),
            type: 'htmlEmbed',
            image: 'html-embed.png',
            categories: [GENERAL],
            settings: {
                rawIframe: '',
                src: '',
                frameWidth: 0,
                frameHeight: 0,
            },
            id: 'embedIframeCode',
        },
        {
            name: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? PS_CUSTOM_WIDGET_SETTINGS : PS_CUSTOM_WIDGET_SETTINGS_AEU,
            isSuperOnly: true,
            id: 'professionalServicesCustomWidget',
        },
        {
            name: $t('widgetCatalogSettings.Top 10 Features by Visitors'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                aggPipeline: JSON.stringify(top10FeaturesByVisitorsAgg),
                config: JSON.stringify(top10FeaturesByVisitorsConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                aggPipeline: JSON.stringify(top10FeaturesByVisitorsAeuAgg),
                config: JSON.stringify(top10FeaturesByVisitorsConfig),
            },
            isSuperOnly: true,
            id: 'top10FeaturesByVisitors',
        },
        {
            name: $t('widgetCatalogSettings.Top 10 Features by Events'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                aggPipeline: JSON.stringify(top10FeaturesByEventsAgg),
                config: JSON.stringify(top10FeaturesByEventsConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                aggPipeline: JSON.stringify(top10FeaturesByEventsAeuAgg),
                config: JSON.stringify(top10FeaturesByEventsConfig),
            },
            isSuperOnly: true,
            id: 'top10FeaturesByEvents',
        },
        {
            name: $t('widgetCatalogSettings.Top 10 Pages by Visitors'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                aggPipeline: JSON.stringify(top10PagesByVisitorsAgg),
                config: JSON.stringify(top10FeaturesByVisitorsConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                aggPipeline: JSON.stringify(top10PagesByVisitorsAeuAgg),
                config: JSON.stringify(top10FeaturesByVisitorsConfig),
            },
            isSuperOnly: true,
            id: 'top10PagesByVisitors',
        },
        {
            name: $t('widgetCatalogSettings.Top 10 Pages by Events'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                aggPipeline: JSON.stringify(top10PagesByEventsAgg),
                config: JSON.stringify(top10PagesByEventsConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                aggPipeline: JSON.stringify(top10PagesByEventsAeuAgg),
                config: JSON.stringify(top10PagesByEventsConfig),
            },
            isSuperOnly: true,
            id: 'top10PagesByEvents',
        },
        {
            name: $t('widgetCatalogSettings.Average Daily Time on App'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                period: 'dayRange',
                aggPipeline: JSON.stringify(averageDailyTimeOnAppAgg),
                config: JSON.stringify(avgDailyTimeOnAppConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                period: 'dayRange',
                aggPipeline: JSON.stringify(averageDailyTimeOnAppAeuAgg),
                config: JSON.stringify(avgDailyTimeOnAppConfig),
            },
            isSuperOnly: true,
            id: 'averageDailyTimeOnApp',
        },
        {
            name: $t('widgetCatalogSettings.Average Weekly Time on App'),
            description: $t('widgetCatalogSettings.Professional Services Custom Widget'),
            type: 'professionalServicesCustomWidget',
            image: 'pendo-logo.png',
            categories: [GENERAL],
            settings: accountsUIEnabled ? {
                ...PS_CUSTOM_WIDGET_SETTINGS,
                period: 'weekRange',
                aggPipeline: JSON.stringify(averageWeeklyTimeOnAppAgg),
                config: JSON.stringify(avgWeeklyTimeOnAppConfig),
            } : {
                ...PS_CUSTOM_WIDGET_SETTINGS_AEU,
                period: 'weekRange',
                aggPipeline: JSON.stringify(averageWeeklyTimeOnAppAeuAgg),
                config: JSON.stringify(avgWeeklyTimeOnAppConfig),
            },
            isSuperOnly: true,
            id: 'averageWeeklyTimeOnApp',
        },
        getNpsWidgetCatalogSettings(),
        {
            name: $t('widgetCatalogSettings.Guide Engagement'),
            description: $t(
                'widgetCatalogSettings.View the total number of first-time and repeat views or visitors for a specific guide.',
            ),
            type: 'guideEngagement',
            image: 'guide-engagement.png',
            categories: [GUIDES],
            settings: {
                segmentId: 'everyone',
                dateRange: RANGE_LAST_30_DAYS,
                period: 'weekly',
                kind: 'viewCount',
                features: [],
            },
            productLines: [[PRODUCT_LINES.GUIDES, 'guideWidgetsEnabled']],
            id: 'guideEngagement',
        },
        {
            name: $t('widgetCatalogSettings.Replay view'),
            description: $t('widgetCatalogSettings.View recent replays within a saved view of your choosing.'),
            type: 'replaySavedFilter',
            image: 'replay-saved-filters.png',
            categories: [REPLAY],
            settings: { savedFilterId: null },
            isPaidPendoOnly: true,
            productLines: ['sessionReplay'],
            id: 'replaySavedFilter',
        },
        ...(!isAdoptEndUser ? [{
            name: $t('widgetCatalogSettings.User License Utilization'),
            description: $t(
                'widgetCatalogSettings.Understand what percentage of visitors who are eligible to complete a certain action have completed that action, by day, week, month, or quarter.',
            ),
            type: 'licenseUtilization',
            image: 'user-license-utilization.png',
            categories: [VISITORS],
            settings: accountsUIEnabled ? {
                segmentId: 'everyone',
                account: DEFAULT_ACCOUNT_SETTING,
                dateRange: RANGE_LAST_30_DAYS,
                weekendsHidden: false,
                completePeriodsOnly: true,
                kind: 'visitor',
                periods: ['today', 'yesterday', 'day', 'week', 'month', 'quarter', 'aggregate'],
            } : {
                segmentId: 'everyone',
                dateRange: RANGE_LAST_30_DAYS,
                weekendsHidden: false,
                completePeriodsOnly: true,
                kind: 'visitor',
                periods: ['today', 'yesterday', 'day', 'week', 'month', 'quarter', 'aggregate'],
            },
            isPaidPendoOnly: true,
            productLines: [PRODUCT_LINES.ANALYTICS],
            id: 'userLicenseUtilization',
        }] : []),
        {
            name: $t('widgetCatalogSettings.Workflow'),
            description: $t(
                'widgetCatalogSettings.Get a quick overview of which visitors are completing your workflow and how long it takes.',
            ),
            type: 'workflowWidget',
            image: 'workflow.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {},
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'workflowEnabled']],
            id: 'workflow',
        },
        {
            name: $t('widgetCatalogSettings.Workflow Journeys'),
            description: $t(
                'widgetCatalogSettings.Uncover potential obstacles in a user’s journey and take actions based on insights. Requires creating or choosing a workflow.',
            ),
            type: 'workflowWidget',
            image: 'workflow-journeys.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {},
            isPaidPendoOnly: true,
            productLines: [[PRODUCT_LINES.ANALYTICS, 'workflowEnabled']],
            id: 'workflowJourneys',
        },
        {
            name: $t('widgetCatalogSettings.AI agent'),
            description: $t(
                'widgetCatalogSettings.aiAgentWidgetDescription',
            ),
            type: 'aiAgentWidget',
            image: 'ai-agent-report.png',
            categories: [AGENT_ANALYTICS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: false,
            productLines: [PRODUCT_LINES.AGENT_ANALYTICS],
            id: 'aiAgent',
        },
        {
            name: $t('widgetCatalogSettings.aiAgentFeedbackSummaryName'),
            description: $t(
                'widgetCatalogSettings.aiAgentFeedbackSummaryDescription',
            ),
            type: 'aiAgentWidget',
            image: 'ai-agent-report.png',
            categories: [REPORTS],
            searchKeywords: ['report', 'feedback', 'agent'],
            settings: {
                reportId: null,
                aiAgentComponentType: 'feedbackChart',
            },
            isPaidPendoOnly: false,
            productLines: [PRODUCT_LINES.AGENT_ANALYTICS],
            segmentFlag: 'aiAgentNewWidgets',
            id: 'aiAgentFeedbackSummary',
        },
        {
            name: $t('widgetCatalogSettings.AI quality'),
            description: $t(
                'widgetCatalogSettings.aiAgentQualityWidgetDescription',
            ),
            type: 'aiAgentQualityWidget',
            image: 'ai-agent-quality.png',
            categories: [REPORTS],
            searchKeywords: ['report'],
            settings: {
                reportId: null,
            },
            isPaidPendoOnly: false,
            productLines: [PRODUCT_LINES.AGENT_ANALYTICS],
            segmentFlag: 'aiAgentNewWidgets',
            id: 'aiAgentQuality',
        },
    ];
};
